import getConfiguration from '@/store/getConfiguration';
import { GetRuleSetsOutputDTO, RuleApi, WriteRuleSetRequest } from '@/api';
import { showError, showSuccess } from '@/mixins/toastService';
import * as Sentry from '@sentry/vue';

async function getApi() {
  const configuration = await getConfiguration();
  return new RuleApi(configuration, '');
}

type InitialState = {
  rules: GetRuleSetsOutputDTO;
  selectedRule: GetRuleSetsOutputDTO | null;
  fetchAbortController: AbortController | null;
  isLoading: boolean;
};

export const initialState = (): InitialState => ({
  rules: { rule_sets: [] },
  isLoading: false,
  selectedRule: null,
  fetchAbortController: null,
});
export type DockRulesState = ReturnType<typeof initialState>;
type ActionType = {
  commit: Function;
  state: DockRulesState;
};

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    resetState(state: DockRulesState) {
      Object.assign(state, initialState());
    },

    setRules(state: DockRulesState, rules: GetRuleSetsOutputDTO) {
      state.rules = rules;
    },
    setLoading(state: DockRulesState, isLoading: boolean) {
      state.isLoading = isLoading;
    },
    setSelectedRule(state: DockRulesState, rule: GetRuleSetsOutputDTO | null) {
      state.selectedRule = rule;
    },

    optimisticallyRemoveRule(state: DockRulesState, ruleId: string) {
      state.rules.rule_sets = state.rules.rule_sets.filter(
        (rule) => rule.id !== ruleId,
      );
    },
    optimisticallyAddRule(state: DockRulesState, rule: GetRuleSetsOutputDTO) {
      state.rules.rule_sets.push(...rule.rule_sets);
    },
    optimisticallyUpdateRule(
      state: DockRulesState,
      updatedRule: GetRuleSetsOutputDTO['rule_sets'][number],
    ) {
      const index = state.rules.rule_sets.findIndex(
        (rule) => rule.id === updatedRule.id,
      );
      if (index !== -1) {
        state.rules.rule_sets.splice(index, 1, updatedRule);
      }
    },
  },
  actions: {
    async fetchRuleById({ commit }: ActionType, ruleId: string) {
      try {
        commit('setLoading', true);
        const api = await getApi();
        const { data } = await api.getRuleSetDockschedulingRulesRuleSetIdGet(
          ruleId,
        );
        commit('setSelectedRule', data);
      } catch (error) {
        showError('dock-management.error-fetching-rule');
        Sentry.captureException(error);
      } finally {
        commit('setLoading', false);
      }
    },
    async fetchRules(
      { commit, state }: ActionType,
      {
        locationId,
        dockIds,
        skipLoadingState,
      }: {
        locationId?: string;
        dockIds?: string[];
        skipLoadingState?: boolean;
      } = { skipLoadingState: false },
    ) {
      state.fetchAbortController?.abort();
      const controller = new AbortController();
      state.fetchAbortController = controller;

      commit('setLoading', !skipLoadingState);

      try {
        const api = await getApi();
        const { data } = await api.listRuleSetsDockschedulingRulesGet(
          locationId,
          dockIds,
          {
            signal: controller.signal,
          },
        );

        if (!controller.signal.aborted) {
          commit('setRules', data);
        }
      } catch (error: any) {
        if (!controller.signal.aborted) {
          showError('dock-management.error-fetching-rules');
        }
        Sentry.captureException(error);
      } finally {
        if (!controller.signal.aborted) {
          commit('setLoading', false);
        }
      }
    },
    async removeRule({ commit }: ActionType, ruleId: string) {
      try {
        commit('setLoading', true);
        const api = await getApi();
        await api.deleteRuleSetDockschedulingRulesRuleSetIdDelete(ruleId);
        showSuccess('dock-management.success-deleting-rule');
        commit('optimisticallyRemoveRule', ruleId);
      } catch (error) {
        showError('dock-management.error-deleting-rule');
        Sentry.captureException(error);
      } finally {
        commit('setLoading', false);
      }
    },

    async createRule({ commit }: ActionType, payload: WriteRuleSetRequest) {
      try {
        commit('setLoading', true);
        const api = await getApi();
        await api.createRuleSetDockschedulingRulesPost(payload);
        showSuccess('dock-management.success-creating-rule');
      } catch (error) {
        showError('dock-management.error-creating-rule');
        Sentry.captureException(error);
      } finally {
        commit('setLoading', false);
      }
    },

    async updateRule(
      { commit }: ActionType,
      payload: WriteRuleSetRequest & { id: string },
    ) {
      try {
        commit('setLoading', true);
        const api = await getApi();
        if (!payload.id) {
          throw new Error('Rule ID is required for update');
        }
        const updatedRule =
          await api.updateRuleSetDockschedulingRulesRuleSetIdPut(
            payload.id,
            payload,
          );

        showSuccess('dock-management.success-updating-rule');
        commit('optimisticallyUpdateRule', updatedRule.data.rule_set);
      } catch (error) {
        showError('dock-management.error-updating-rule');
        Sentry.captureException(error);
      } finally {
        commit('setLoading', false);
      }
    },
  },
  getters: {
    getRules(state: DockRulesState) {
      const map = state.rules.rule_sets.reduce<
        Record<string, GetRuleSetsOutputDTO['rule_sets'][number][]>
      >((acc, rule) => {
        const locationId = rule.location.id;
        acc[locationId] ??= [];
        acc[locationId].push(rule);
        return acc;
      }, {});

      return Object.entries(map).map(([location, rules]) => ({
        location,
        locationName: rules[0]?.location.name || 'Unknown Location',
        rules,
      }));
    },
  },
};
